import { useEffect, useState } from 'react'
import { useSearch } from '@tanstack/react-router'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { UnsubscribeForm } from './unsubscribe-form'
import { apiClient, ApiClientError } from '@/lib/api-client'
import type {
  UnsubscribePreferencesResponse,
  UnsubscribeSuccessResponse,
  EmailPreferences,
} from '@/types/unsubscribe'

type PageState = 'loading' | 'form' | 'success' | 'error'

interface ErrorState {
  message: string
  isExpired?: boolean
}

export function UnsubscribePage() {
  const search = useSearch({ from: '/unsubscribe/' }) as {
    token?: string
    email?: string
  }
  const [state, setState] = useState<PageState>('loading')
  const [userPrefs, setUserPrefs] =
    useState<UnsubscribePreferencesResponse | null>(null)
  const [successResponse, setSuccessResponse] =
    useState<UnsubscribeSuccessResponse | null>(null)
  const [error, setError] = useState<ErrorState | null>(null)

  const token = search.token
  const email = search.email

  useEffect(() => {
    if (!token || !email) {
      setError({
        message:
          'Invalid unsubscribe link. Please use the link from your email.',
      })
      setState('error')
      return
    }

    const fetchUserPreferences = async () => {
      try {
        setState('loading')
        const response = await apiClient.getUnsubscribePreferences({
          token,
          email,
        })
        setUserPrefs(response)
        setState('form')
      } catch (err) {
        console.error('Failed to load preferences:', err)

        let errorMessage = 'Failed to load preferences. Please try again.'
        let isExpired = false

        if (err instanceof ApiClientError) {
          switch (err.statusCode) {
            case 400:
              errorMessage =
                'Invalid or expired unsubscribe link. Please try again with a fresh link from your email.'
              isExpired = true
              break
            case 404:
              errorMessage =
                'Account not found. This unsubscribe link may be invalid.'
              break
            case 429:
              errorMessage =
                'Too many requests. Please wait a moment and try again.'
              break
            default:
              errorMessage = err.message || errorMessage
          }
        }

        setError({ message: errorMessage, isExpired })
        setState('error')
      }
    }

    fetchUserPreferences()
  }, [token, email])

  const handleSuccess = (response: UnsubscribeSuccessResponse) => {
    setSuccessResponse(response)
    setState('success')
  }

  const handleError = (errorMessage: string) => {
    setError({ message: errorMessage })
    setState('error')
  }

  const handleResubscribeAll = async () => {
    if (!token || !email) return

    try {
      setState('loading')
      const allEnabled: EmailPreferences = {
        marketing: true,
        account: true,
        product: true,
        community: true,
        system: true,
      }

      const response = await apiClient.updateUnsubscribePreferences({
        token,
        email,
        preferences: allEnabled,
      })

      setSuccessResponse(response)
      setState('success')
    } catch (err) {
      console.error('Failed to resubscribe:', err)
      handleError('Failed to resubscribe. Please try again.')
    }
  }

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-center text-muted-foreground">
              Loading your preferences...
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-destructive">
              {error?.isExpired ? 'Link Expired' : 'Error'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">{error?.message}</p>

            {error?.isExpired && (
              <div className="space-y-3">
                <p className="text-sm">To manage your email preferences:</p>
                <ol className="text-sm space-y-1 list-decimal list-inside text-muted-foreground">
                  <li>Log into your account at thoughtfabric.ai</li>
                  <li>Go to your account settings</li>
                  <li>Update your email preferences</li>
                </ol>
                <Button
                  onClick={() => (window.location.href = '/login')}
                  className="w-full"
                >
                  Go to Login
                </Button>
              </div>
            )}

            {!error?.isExpired && (
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full"
              >
                Try Again
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (state === 'success' && successResponse) {
    const allDisabled = Object.values(successResponse.preferences).every(
      v => !v
    )

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-green-600">
              Preferences Updated
            </CardTitle>
            <CardDescription>{successResponse.message}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!allDisabled && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  You can modify your preferences again anytime using this link.
                </p>
                <Button
                  onClick={() => setState('form')}
                  variant="outline"
                  className="w-full"
                >
                  Modify Preferences
                </Button>
              </div>
            )}

            {allDisabled && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  If you change your mind, you can resubscribe anytime.
                </p>
                <Button onClick={handleResubscribeAll} className="w-full">
                  Resubscribe to All
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (state === 'form' && userPrefs) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="container mx-auto py-8 max-w-4xl">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold mb-2">
              Email Preferences
            </h1>
            <p className="text-muted-foreground text-sm md:text-base">
              Manage your email subscriptions and preferences
            </p>
          </div>

          <UnsubscribeForm
            userPrefs={userPrefs}
            token={token!}
            email={email!}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        </div>
      </div>
    )
  }

  return null
}
